import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IonAlert } from '@ionic/react';
import { isProbablyDesktop } from '../../device-detect';

type BeforeInstallPromptEvent = Event & {
	prompt: () => Promise<void>;
	userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string | string[] }>;
};

const STORAGE_KEY = 'a2hs-dismissed-at';
const DISMISS_TTL_MS = 1000 * 60 * 60 * 24 * 3; // 3 days

type PromptType = 'android' | 'android_manual' | 'ios';

function isIosDevice(): boolean {
	if (typeof navigator === 'undefined') return false;
	const ua = navigator.userAgent || '';
	const platform = navigator.platform || '';
	const maxTouchPoints = navigator.maxTouchPoints || 0;
	const iOSRegex = /iPad|iPhone|iPod/i.test(ua);
	const isMacWithTouch = platform === 'MacIntel' && maxTouchPoints > 1;
	return iOSRegex || isMacWithTouch;
}

function isAndroidDevice(): boolean {
	if (typeof navigator === 'undefined') return false;
	return /Android/i.test(navigator.userAgent || '');
}

const AddToHomePrompt = () => {
	const [deferredEvent, setDeferredEvent] = useState<BeforeInstallPromptEvent | null>(null);
	const [isOpen, setIsOpen] = useState(false);
	const [promptType, setPromptType] = useState<PromptType | null>(null);
	const [dismissedAt, setDismissedAt] = useState<number | null>(() => {
		if (typeof window === 'undefined') {
			return null;
		}
		const stored = localStorage.getItem(STORAGE_KEY);
		if (!stored) {
			return null;
		}
		const timestamp = Number(stored);
		return Number.isFinite(timestamp) ? timestamp : null;
	});
	const deferredEventRef = useRef<BeforeInstallPromptEvent | null>(null);

	const isEligible = useMemo(() => {
		if (typeof window === 'undefined') return false;
		if (window.matchMedia?.('(display-mode: standalone)').matches) return false;
		if ((window.navigator as any).standalone === true) return false;
		if (dismissedAt === null) {
			return !isProbablyDesktop();
		}
		return !isProbablyDesktop() && Date.now() - dismissedAt >= DISMISS_TTL_MS;
	}, [dismissedAt]);

	useEffect(() => {
		if (!isEligible) return;

		let iosTimer: number | undefined;
		let androidFallbackTimer: number | undefined;

		const handleBeforeInstallPrompt = (event: Event) => {
			event.preventDefault();
			deferredEventRef.current = event as BeforeInstallPromptEvent;
			setDeferredEvent(event as BeforeInstallPromptEvent);
			setPromptType('android');
			setIsOpen(true);
		};

		const handleAppInstalled = () => {
			setIsOpen(false);
			setDeferredEvent(null);
			deferredEventRef.current = null;
			setPromptType(null);
			localStorage.removeItem(STORAGE_KEY);
			setDismissedAt(null);
		};

		window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
		window.addEventListener('appinstalled', handleAppInstalled);

		if (isIosDevice()) {
			iosTimer = window.setTimeout(() => {
				setPromptType((current) => current ?? 'ios');
				setIsOpen(true);
			}, 2000);
		}

		if (isAndroidDevice()) {
			androidFallbackTimer = window.setTimeout(() => {
				if (deferredEventRef.current !== null) {
					return;
				}
				setPromptType((current) => current ?? 'android_manual');
				setIsOpen(true);
			}, 3000);
		}

		return () => {
			window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
			window.removeEventListener('appinstalled', handleAppInstalled);
			if (iosTimer) {
				window.clearTimeout(iosTimer);
			}
			if (androidFallbackTimer) {
				window.clearTimeout(androidFallbackTimer);
			}
		};
	}, [isEligible]);

	const closePrompt = useCallback(() => {
		setIsOpen(false);
		setPromptType(null);
		setDeferredEvent(null);
	}, []);

	const handleInstall = useCallback(async () => {
		if (!deferredEvent) return;
		closePrompt();
		deferredEvent.prompt();
		try {
			await deferredEvent.userChoice;
		} finally {
			deferredEventRef.current = null;
			setDeferredEvent(null);
		}
	}, [closePrompt, deferredEvent]);

	const handleDismiss = useCallback(() => {
		closePrompt();
		deferredEventRef.current = null;
		const now = Date.now();
		localStorage.setItem(STORAGE_KEY, now.toString());
		setDismissedAt(now);
	}, [closePrompt]);

	if (!isOpen || !promptType) return null;

	const isIos = promptType === 'ios';
	const isAndroidManual = promptType === 'android_manual';
	const canPromptInstall = promptType === 'android' && deferredEvent !== null;

	return (
		<IonAlert
			isOpen={isOpen}
			header="Add to your home screen"
			message={
				isIos
					? 'Tap the share icon in Safari and choose "Add to Home Screen" to install this app.'
					: isAndroidManual
					? 'Use your browser menu and choose "Install app" or "Add to Home screen" to install this app.'
					: 'Install the Trails app for faster access, offline support, and a full-screen experience.'
			}
			buttons={
				isIos || isAndroidManual || !canPromptInstall
					? [
						{
							text: 'Got it',
							role: 'confirm',
							handler: handleDismiss,
						},
					]
					: [
						{
							text: 'Maybe later',
							role: 'cancel',
							handler: handleDismiss,
						},
						{
							text: 'Install',
							handler: handleInstall,
						},
					]
			}
			backdropDismiss={false}
		/>
	);
};

export default AddToHomePrompt;
